import { useCallback, useEffect, useState } from 'react';

/**
 * Browser permission state, normalized across the Permissions API and the
 * Notifications API.
 *
 * - `granted`      — the site may use the capability.
 * - `prompt`       — not yet decided; a request can still surface the OS/browser prompt.
 * - `denied`       — blocked; the browser will NOT re-prompt. Recovery is manual
 *                    via the site-settings control in the address bar (there is no
 *                    programmatic deep-link on the web).
 * - `unsupported`  — the browser can't report this permission (e.g. Firefox/Safari
 *                    don't expose `camera` to `permissions.query`).
 */
export type PermState = 'granted' | 'prompt' | 'denied' | 'unsupported';
export type PermName = 'geolocation' | 'camera' | 'notifications';

function readNotifications(): PermState {
  if (typeof Notification === 'undefined') return 'unsupported';
  // Notification.permission: 'default' | 'granted' | 'denied'.
  const p = Notification.permission;
  return p === 'default' ? 'prompt' : (p as PermState);
}

/**
 * Live browser-permission state for [name], without triggering any prompt.
 *
 * Backed by `navigator.permissions.query` (with its `change` event) where
 * available; notifications fall back to `Notification.permission`. `refresh()`
 * re-reads on demand — useful for notifications (which fire no change event) and
 * after returning from the browser's site-settings UI.
 */
export function usePermission(name: PermName): {
  state: PermState;
  refresh: () => void;
} {
  const [state, setState] = useState<PermState>('unsupported');

  const read = useCallback(async () => {
    if (name === 'notifications') {
      setState(readNotifications());
      return;
    }
    if (!('permissions' in navigator) || !navigator.permissions?.query) {
      setState('unsupported');
      return;
    }
    try {
      const status = await navigator.permissions.query({
        name: name as PermissionName,
      });
      setState(status.state as PermState);
    } catch {
      // The browser doesn't recognize this permission name (common for camera).
      setState('unsupported');
    }
  }, [name]);

  useEffect(() => {
    let cancelled = false;
    let status: PermissionStatus | undefined;
    const onChange = () => read();

    read();

    if (
      name !== 'notifications' &&
      'permissions' in navigator &&
      navigator.permissions?.query
    ) {
      navigator.permissions
        .query({ name: name as PermissionName })
        .then((s) => {
          if (cancelled) return;
          status = s;
          status.addEventListener('change', onChange);
        })
        .catch(() => {
          /* unsupported — nothing to subscribe to */
        });
    }

    // Notifications emit no change event; re-read when the tab regains focus so
    // a grant/revoke done in the browser's site settings is reflected.
    const onFocus = () => read();
    if (name === 'notifications') window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      status?.removeEventListener('change', onChange);
      if (name === 'notifications') window.removeEventListener('focus', onFocus);
    };
  }, [name, read]);

  return { state, refresh: read };
}

export default usePermission;
